import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react'

type ToastLevel = 'success' | 'error' | 'info'

/** Acción reversible opcional (§5.6): "Deshacer". Extiende la vida a 9s. */
export interface ToastAction {
  label: string
  onClick: () => void
}
interface ToastOptions {
  action?: ToastAction
  /** Sobrescribe la duración (ms). Default 5000, o 9000 con acción. */
  durationMs?: number
}

type Toast = {
  id: number
  level: ToastLevel
  message: string
  detail?: string
  action?: ToastAction
  durationMs: number
}

type ToastContextValue = {
  push: (level: ToastLevel, message: string, detail?: string, options?: ToastOptions) => void
  success: (message: string, detail?: string, options?: ToastOptions) => void
  error: (message: string, detail?: string, options?: ToastOptions) => void
  info: (message: string, detail?: string, options?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let _seq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback(
    (level: ToastLevel, message: string, detail?: string, options?: ToastOptions) => {
      const id = ++_seq
      const durationMs = options?.durationMs ?? (options?.action ? 9000 : 5000)
      setToasts((prev) => [...prev, { id, level, message, detail, action: options?.action, durationMs }])
    },
    [],
  )

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const value: ToastContextValue = {
    push,
    success: (msg, det, opt) => push('success', msg, det, opt),
    error: (msg, det, opt) => push('error', msg, det, opt),
    info: (msg, det, opt) => push('info', msg, det, opt),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-60 flex flex-col gap-2 max-w-sm" role="status" aria-live="polite">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  useEffect(() => {
    const handle = setTimeout(onClose, toast.durationMs)
    return () => clearTimeout(handle)
  }, [onClose, toast.durationMs])

  const palette = LEVEL_PALETTE[toast.level]
  const Icon = palette.icon

  return (
    <div
      className={`flex items-start gap-2 rounded-lg border border-l-4 bg-card px-3 py-2.5 shadow-lg ${palette.border} ${palette.text}`}
    >
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-fg">{toast.message}</p>
        {toast.detail && <p className="text-xs text-fg-2 mt-0.5 wrap-break-word">{toast.detail}</p>}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action!.onClick()
              onClose()
            }}
            className="mt-1.5 text-xs font-semibold text-brand hover:text-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="text-muted hover:text-fg transition shrink-0"
        aria-label="Cerrar"
      >
        <X size={14} />
      </button>
    </div>
  )
}

// Borde izquierdo 4px por familia semántica + ícono en el color del tono (§5.6).
const LEVEL_PALETTE: Record<ToastLevel, { border: string; text: string; icon: typeof CheckCircle2 }> = {
  success: { border: 'border-ok-line border-l-ok-fg', text: 'text-ok-fg', icon: CheckCircle2 },
  error: { border: 'border-err-line border-l-err-fg', text: 'text-err-fg', icon: AlertTriangle },
  info: { border: 'border-info-line border-l-info-fg', text: 'text-info-fg', icon: Info },
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>')
  return ctx
}
